import { useEffect } from 'react';

interface CareersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const openings = [
  {
    icon: 'ri-truck-line',
    title: 'Refrigerated Truck Driver',
    type: 'Full-Time',
    category: 'Transportation',
    description:
      'Operate temperature-controlled vehicles across Canadian provinces. Valid Class 1 / AZ licence required. Open to skilled foreign workers through LMIA-supported pathways.',
    tags: ['Class 1 / AZ', 'Long-Haul', 'LMIA Available'],
  },
  {
    icon: 'ri-building-2-line',
    title: 'Cold Storage Warehouse Associate',
    type: 'Full-Time / Part-Time',
    category: 'Warehousing',
    description:
      'Loading, unloading and organising temperature-sensitive goods. No prior cold-storage experience required — full on-site training provided. Open to newcomers and permanent residents.',
    tags: ['No Experience Required', 'Training Provided', 'Newcomers Welcome'],
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Logistics Coordinator',
    type: 'Full-Time',
    category: 'Operations',
    description:
      'Plan and optimise transport routes, coordinate delivery schedules and liaise with clients. Proficiency in logistics software and strong communication skills required.',
    tags: ['Route Planning', 'TMS Software', 'Client Facing'],
  },
  {
    icon: 'ri-temp-hot-line',
    title: 'Refrigeration Technician',
    type: 'Full-Time',
    category: 'Maintenance',
    description:
      'Maintain and repair refrigeration units across our fleet and warehouse facilities. Red Seal certification preferred. Candidates with equivalent foreign credentials are encouraged to apply.',
    tags: ['Red Seal Preferred', 'Fleet & Facility', 'Foreign Credentials Considered'],
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Food Safety & Compliance Officer',
    type: 'Full-Time',
    category: 'Quality & Compliance',
    description:
      'Oversee HACCP, ISO 22000 and SQF compliance across all operational sites. Experience in a regulated food supply-chain environment required.',
    tags: ['HACCP', 'ISO 22000', 'Regulatory Affairs'],
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Account Manager – Logistics',
    type: 'Full-Time',
    category: 'Sales & Business Development',
    description:
      'Develop and manage relationships with food producers, retailers and distributors. Grow the client portfolio and negotiate service agreements.',
    tags: ['B2B Sales', 'CRM', 'Food Industry'],
  },
];

const stats = [
  { icon: 'ri-group-line', value: '800+', label: 'Team Members' },
  { icon: 'ri-briefcase-line', value: '25+', label: 'Open Positions' },
  { icon: 'ri-global-line', value: '20+', label: 'Countries Represented' },
  { icon: 'ri-map-2-line', value: 'Canada-Wide', label: 'Hiring Locations' },
];

export default function CareersModal({ isOpen, onClose }: CareersModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header image */}
        <div className="relative h-56 md:h-72 rounded-t-2xl overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/fe6e66d3c64ad300bd0480746c6e35c7.jpg"
            alt="Careers at McALLISTER DELIGHT"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-[#0F172A]/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <p className="text-[#DC2626] text-xs font-bold uppercase tracking-widest mb-2">
              JOIN OUR TEAM
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Careers at McALLISTER DELIGHT
            </h2>
          </div>
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-8 md:p-10">

          {/* Intro */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-[#1A365D] mb-4">We Are Growing — Come Build With Us</h3>
            <p className="text-gray-600 leading-relaxed text-base mb-4">
              McALLISTER DELIGHT is in an active phase of expansion across Canada. As we scale our
              refrigerated transport fleet, extend our cold-storage footprint and broaden our
              distribution network, we are continuously adding talented people at every level of
              the organisation — from drivers and warehouse operators to logistics coordinators,
              engineers and commercial leads.
            </p>
            <p className="text-gray-600 leading-relaxed text-base mb-4">
              We welcome applications from <strong className="text-[#1A365D]">Canadian citizens, permanent residents and
              foreign nationals</strong>. For eligible international candidates, we actively support
              work permit and LMIA processes in partnership with authorised immigration consultants.
              If you bring the right skills and drive, we want to hear from you — regardless of where
              you are coming from.
            </p>
            <div className="bg-[#f0f4f8] border-l-4 border-[#C8102E] rounded-r-lg px-5 py-4">
              <p className="text-gray-700 leading-relaxed text-base">
                <strong className="text-[#C8102E]">Atlantic Immigration Program (AIP)&nbsp;—</strong>{' '}
                McALLISTER DELIGHT is a <strong className="text-[#1A365D]">designated employer</strong> under
                Canada's Atlantic Immigration Program. This federal pathway allows us to hire qualified
                foreign nationals for permanent residence faster and more directly than through
                standard immigration streams. If you are interested in relocating to Atlantic Canada and
                building a long-term career with us, the AIP may be the right route for you. Our HR team
                will guide eligible candidates through every step of the process.
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {stats.map((s, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-[#DC2626]/10 rounded-full">
                  <i className={`${s.icon} text-[#DC2626] text-xl`}></i>
                </div>
                <div className="text-xl font-bold text-[#1A365D] mb-1">{s.value}</div>
                <div className="text-xs text-gray-500 font-medium">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Open Positions */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-[#1A365D] mb-2">Current Openings</h3>
            <p className="text-gray-500 text-sm mb-6">
              A representative selection of active roles. Additional positions are available — contact us or reach out via our recruitment partners for a full list.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {openings.map((job, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-5 bg-gray-50 hover:border-[#DC2626]/30 transition-colors">
                  <div className="flex items-start space-x-4 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#DC2626] to-[#F97316] rounded-full">
                      <i className={`${job.icon} text-white text-base`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#1A365D] text-sm leading-snug">{job.title}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{job.category} · {job.type}</div>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed mb-3">{job.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {job.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded-full bg-white border border-gray-200 text-gray-500 text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recruitment Partners */}
          <div className="rounded-xl bg-[#1A365D]/5 border border-[#1A365D]/10 p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#1A365D] rounded-full">
                <i className="ri-team-line text-white text-base"></i>
              </div>
              <div>
                <h4 className="font-bold text-[#1A365D] text-sm mb-1">External Recruitment Partners</h4>
                <p className="text-gray-600 text-xs leading-relaxed">
                  McALLISTER DELIGHT collaborates with a network of authorised external recruitment and staffing agencies
                  specialising in logistics, transportation and cold-chain operations. Our partners assist with candidate
                  sourcing for both domestic and international hires, including immigration support for skilled foreign
                  workers. All official opportunities are posted through verified channels.
                </p>
              </div>
            </div>
          </div>

          {/* How to Apply */}
          <div className="border-t border-gray-100 pt-7">
            <h3 className="text-xl font-bold text-[#1A365D] mb-4">How to Apply</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                {
                  step: '01',
                  icon: 'ri-file-user-line',
                  title: 'Submit Your CV',
                  desc: 'You need to send a package of your documents for review.',
                },
                {
                  step: '02',
                  icon: 'ri-search-eye-line',
                  title: 'Application Review',
                  desc: 'Our HR team or a recruitment partner will review your profile and match it against current and upcoming openings.',
                },
                {
                  step: '03',
                  icon: 'ri-check-double-line',
                  title: 'Offer & Onboarding',
                  desc: 'Successful candidates receive a formal offer and are guided through the full onboarding process, including any work-permit requirements.',
                },
              ].map((step, i) => (
                <div key={i} className="text-center p-5 rounded-xl bg-gray-50">
                  <div className="text-3xl font-black text-[#DC2626]/20 mb-2">{step.step}</div>
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-[#DC2626]/10 rounded-full">
                    <i className={`${step.icon} text-[#DC2626] text-lg`}></i>
                  </div>
                  <div className="font-semibold text-[#1A365D] text-sm mb-2">{step.title}</div>
                  <div className="text-gray-500 text-xs leading-relaxed">{step.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
